package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.CreateQaIgnoreRequest
import dev.lingvo.shared.CreateQaIgnoresRequest
import dev.lingvo.shared.MAX_QA_IGNORES_PER_REQUEST
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.QaCheck
import dev.lingvo.shared.QaIgnoreTarget
import dev.lingvo.shared.QaIgnoresCreatedDto
import dev.lingvo.shared.QaIssueDto
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.QaSummaryDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCircleAlertIcon
import dev.lingvo.site.ds.LFreshness
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LRefreshIcon
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToggleChip
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.runProjectAction
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.js.Date
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

/**
 * A ceiling on how many rows one check's group renders.
 *
 * A broken glossary term fires on every string in the project; the page built a DOM node per issue
 * per locale and froze the tab before anybody could read the first one.
 */
private const val MAX_ISSUES_PER_GROUP = 200

/**
 * How long a check report reads as current.
 *
 * Nothing on this page refetches by itself, so "when did this run" is the only honest answer and the
 * refresh control is the only fix. After five minutes it stops being a ghost button — a discrete
 * change of variant, not a ticking clock, because a value that re-renders itself is the animated
 * live figure this product's rules exclude.
 */
private const val STALE_AFTER_MS = 5L * 60L * 1_000L

@Composable
internal fun QaContent(
    project: ProjectDetailDto,
    issues: List<QaIssueDto>,
    authority: ProjectAuthority,
    actionStateKey: Any?,
    mutationClaim: ProjectActionClaim? = null,
    refreshing: Boolean,
    refreshError: String?,
    onRetry: () -> Unit,
    onOpenIssue: (QaIssueDto) -> Unit,
    locale: String,
    onLocaleChange: (String) -> Unit,
    onOpenProject: (() -> Unit)?,
    /**
     * The server's own totals for this scope, when the caller fetched them.
     *
     * **The counts a group header shows are not the rows under it.** `GET /qa` caps its answer at a
     * thousand issues, so on a project that has just been machine-translated a header computed from
     * the rendered list says "200 warnings" for a check with four thousand — and a release manager
     * reads that as nearly settled. [QaSummaryDto.countsByCheck] is counted before the cap;
     * [QaSummaryDto.truncated] is how this page knows to say the list is a page rather than the set.
     *
     * Null for a caller that only has the issue list, in which case the rendered size is the honest
     * total and nothing claims otherwise.
     */
    summary: QaSummaryDto? = null,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(project.slug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    isMutationCurrent: () -> Boolean = {
        mutationClaim?.remainsAuthorized(currentRoute(), currentMemberships()) == true
    },
    addIgnore: suspend (String, CreateQaIgnoreRequest) -> Unit = { projectSlug, request ->
        ApiClient.addQaIgnore(projectSlug, request)
    },
    addIgnores: suspend (String, CreateQaIgnoresRequest) -> QaIgnoresCreatedDto = { projectSlug, request ->
        ApiClient.addQaIgnores(projectSlug, request)
    },
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
    rulesAction: (@Composable () -> Unit)? = null,
) {
    val scope = rememberCoroutineScope()
    val launch: (suspend () -> Unit) -> Unit = launchAction ?: { action ->
        scope.launch { action() }
    }
    var errorsOnly by remember(actionStateKey) { mutableStateOf(false) }
    // The server refuses a blank reason, and rightly: an exemption with no explanation is
    // indistinguishable from the check being wrong, six months later.
    var exempting by remember(actionStateKey) { mutableStateOf<QaIssueDto?>(null) }
    // The check a whole-group exemption is being written for, or null. Deliberately a check rather
    // than a captured list: the targets are recomputed from `visibleIssues` at confirm time, so a
    // refresh landing while the modal is open cannot exempt strings that are no longer failing.
    var exemptingAll by remember(actionStateKey) { mutableStateOf<QaCheck?>(null) }
    var reason by remember(actionStateKey) { mutableStateOf("") }
    var saving by remember(actionStateKey) { mutableStateOf(false) }
    var exemptError by remember(actionStateKey) { mutableStateOf<String?>(null) }
    val canExempt = authority.canExemptQa
    var previouslyCouldExempt by remember(project.id, project.slug) { mutableStateOf(canExempt) }
    // Absent means "this group has not been touched", which is not the same as "expanded": a
    // warning-only group opens collapsed, because nothing in it is blocking a release and nine open
    // advisory groups bury the one that is.
    val expansion = remember(actionStateKey) { mutableStateMapOf<QaCheck, Boolean>() }
    // **When these numbers were read, and a promoted refresh once they are old.** The status line
    // used to claim "checks refresh automatically" while nothing ever refetched. Nothing refetches
    // now either — but the reading time is on screen, and after five minutes the control that would
    // fix it stops being a ghost button.
    val readAt = remember(issues) { Date() }
    var stale by remember(issues) { mutableStateOf(false) }
    LaunchedEffect(issues) {
        stale = false
        delay(STALE_AFTER_MS)
        stale = true
    }
    val targetLocales = (
        project.locales.filter { it != project.sourceLocale } + issues.map(QaIssueDto::locale)
        ).distinct()
        .sortedBy { localeInfo(it).englishName }
    val scopedIssues = issues
        .asSequence()
        .filter { locale.isBlank() || it.locale == locale }
        .toList()
    val visibleIssues = if (errorsOnly) {
        scopedIssues.filter { it.severity == QaSeverity.ERROR }
    } else {
        scopedIssues
    }
    // Counted before the cap where the server sent totals, and from the rows otherwise. The two
    // agree on every project small enough for the cap not to bite, which is most of them.
    val errors = summary?.countsBySeverity?.get(QaSeverity.ERROR.name)
        ?: scopedIssues.count { it.severity == QaSeverity.ERROR }
    val warnings = summary?.countsBySeverity?.get(QaSeverity.WARNING.name)
        ?: (scopedIssues.size - scopedIssues.count { it.severity == QaSeverity.ERROR })
    val affectedChecks = summary?.countsByCheck?.count { it.value > 0 }
        ?: scopedIssues.map(QaIssueDto::check).distinct().size
    val scopeLabel = if (locale.isBlank()) {
        t("editor.qa_page.all_target_locales_scope", "all target locales")
    } else {
        localeInfo(locale).englishName
    }
    // **The counts are stated once**, on the chips that filter by them. They used to appear in the
    // subtitle and again as three badges under it, so the same two numbers were on screen twice with
    // nothing saying they were the same numbers.
    val subtitle = tf(
        "editor.qa_page.what_the_checks_found_in_scope",
        "What the checks found in {scope}.",
        "scope" to scopeLabel,
    )
    // Errors first, then by name, so "the first error" is a stable string rather than whichever one
    // the server happened to return first.
    val firstError = scopedIssues
        .filter { it.severity == QaSeverity.ERROR }
        .minWithOrNull(compareBy<QaIssueDto> { it.keyName.lowercase() }.thenBy(QaIssueDto::locale))

    LaunchedEffect(canExempt) {
        if (previouslyCouldExempt && !canExempt) {
            exempting = null
            exemptError = null
            (document.getElementById("project-qa-readonly-${project.slug}") as? HTMLElement)?.focus()
        }
        previouslyCouldExempt = canExempt
    }

    PageHeader(
        title = t("editor.qa_page.quality_assurance", "Quality assurance"),
        subtitle = subtitle,
        // The production surface supplies the route-aware link. Keeping router access outside this
        // content leaf lets the real issue/modal behavior mount without fabricating a @Page context.
        actions = {
            // **The one press this page exists for.** A QA page whose answer to "what is blocking
            // me" was "read nine collapsed groups and find one" made the reader do the sorting the
            // server had already done. Absent, not disabled, when nothing is failing.
            firstError?.let { issue ->
                LButton(
                    label = t("editor.qa_page.fix_the_first_error", "Fix the first error"),
                    onClick = { onOpenIssue(issue) },
                    leadingIcon = { LWarningIcon() },
                )
            }
            rulesAction?.invoke()
        },
    )
    if (!canExempt) {
        Div(attrs = {
            id("project-qa-readonly-${project.slug}")
            attr("tabindex", "-1")
        }) {
            LAlert(
                title = t("editor.qa_page.qa_exemptions_are_managed_by_an", "QA exemptions are managed by an owner or admin"),
                description = t("editor.qa_page.you_can_inspect_every_issue_and", "You can inspect every issue and open its string without changing the checks this project enforces."),
                variant = LAlertVariant.INFO,
            )
        }
    }
    Div(QaFilterBarStyle.toModifier().toAttrs()) {
        Div(QaFilterControlsStyle.toModifier().toAttrs()) {
            LSelect(
                value = locale,
                options = listOf(LSelectOption("", t("editor.qa_page.all_target_locales", "All target locales"))) + targetLocales.map { code ->
                    val info = localeInfo(code)
                    LSelectOption(code, "${localeFlagPrefix(info.code)}${info.englishName} · $code")  // i18n-ignore — a flag glyph immediately followed by a language name — no separator, no words, nothing a translator could change
                },
                onValueChange = onLocaleChange,
                modifier = QaLocaleSelectStyle.toModifier(),
                label = t("editor.qa_page.locale", "Locale"),
            )
        }
        Column(Modifier.gap(Space.XS)) {
            Div(QaSummaryStyle.toModifier().toAttrs()) {
                // The count and the filter are the same control: a badge saying "12 errors" beside a
                // switch reading "errors only" is two things to press for one intention.
                LToggleChip(
                    text = t("editor.qa_page.errors", "Errors"),
                    selected = errorsOnly,
                    onSelectedChange = { errorsOnly = it },
                    count = errors,
                    icon = { LCircleAlertIcon() },
                )
                LBadge(
                    text = tPlural("editor.qa_page.count.warning", warnings, "{count} warning", "{count} warnings"),
                    variant = if (warnings > 0) LBadgeVariant.WARNING else LBadgeVariant.NEUTRAL,
                    dot = warnings > 0,
                )
                LBadge(
                    text = tPlural(
                        "editor.qa_page.count.check_affected",
                        affectedChecks,
                        "{count} check affected",
                        "{count} checks affected",
                    ),
                    variant = LBadgeVariant.NEUTRAL,
                )
            }
            Div(QaRefreshStatusStyle.toModifier().toAttrs {
                attr("role", "status")
                attr("aria-live", "polite")
            }) {
                // This used to read "Checks refresh automatically" while nothing ever refetched and
                // the only refresh control lived inside the error alert — so a stale report looked
                // live, and there was no way to ask for a fresh one. Say what is true, say when it
                // was true, and offer the button.
                if (refreshing) {
                    Text(t("editor.qa_page.refreshing_checks", "Refreshing checks…"))
                } else {
                    Text(t("editor.qa_page.checks_ran_when_this_page_loaded", "Checks ran when this page loaded"))
                    LFreshness(readAt, Modifier.padding(left = Space.XS))
                }
            }
            LButton(
                label = t("editor.qa_page.refresh_checks", "Refresh checks"),
                onClick = onRetry,
                variant = if (stale) LButtonVariant.SECONDARY else LButtonVariant.GHOST,
                size = LButtonSize.SM,
                enabled = !refreshing,
                leadingIcon = { LRefreshIcon() },
            )
        }
    }
    if (refreshError != null) {
        LAlert(
            title = t("editor.qa_page.showing_the_last_successful_check", "Showing the last successful check"),
            description = refreshError,
            modifier = Modifier.fillMaxWidth(),
            variant = LAlertVariant.WARNING,
            // `refreshError` is only ever set over an already-visible success, and `loadReloadable`
            // only reaches that branch from a `reloadToken` bump — which on this page is the reader
            // pressing "Refresh checks" or "Retry". It can never be on the page at load, so
            // announcing it is information rather than the interruption `live` defaults away from.
            live = true,
            action = {
                LButton(
                    label = t("editor.qa_page.retry", "Retry"),
                    onClick = onRetry,
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                )
            },
        )
    }
    // The server stopped counting out loud. Said here rather than per group, because it is a fact
    // about the request and not about any one check.
    if (summary?.truncated == true) {
        LAlert(
            title = t("editor.qa_page.this_is_a_page_of_the_findings", "This is a page of the findings"),
            description = tf(
                "editor.qa_page.the_checks_found_total_issues_and",
                "The checks found {total} issues and this page holds the first {shown}. Filter to " +
                    "one language to see the rest.",
                "total" to summary.total,
                "shown" to summary.issues.size,
            ),
            modifier = Modifier.fillMaxWidth(),
            variant = LAlertVariant.INFO,
            dense = true,
        )
    }
    when {
        // **"Checks clean" is a claim about the project, so it needs a project-wide answer.**
        // The locale filter used to be applied in the browser over every locale's issues, so an
        // empty list could only mean the project was clean. It is a server-side scope now: pick
        // German on a project with forty French errors and the response is empty, and this branch
        // would put a green "Checks clean" badge on it. With a locale selected, an empty answer
        // falls through to the filtered state, which says so and offers a way back.
        locale.isBlank() && issues.isEmpty() -> QaClearState(project.name, onOpenProject)
        visibleIssues.isEmpty() -> QaFilteredEmptyState(
            errorsOnly = errorsOnly,
            locale = locale,
            onClear = {
                errorsOnly = false
                onLocaleChange("")
            },
        )
        else -> Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            // Server counts are for the whole scope, so they are only the group's true size while
            // the whole scope is on screen. Under "errors only" the rendered rows are the honest
            // total for what the reader asked to see.
            val groups = qaGroups(
                issues = visibleIssues,
                countsByCheck = if (errorsOnly) emptyMap() else summary?.countsByCheck.orEmpty(),
                renderCap = MAX_ISSUES_PER_GROUP,
            )
            // **A group collapses by default only when something else is blocking.** The rule
            // exists so nine advisory groups cannot bury the one holding up a release; on a page
            // where nothing is blocking there is nothing to bury, and closing everything would just
            // hide the findings the reader came to read.
            val blocking = groups.any { it.errors > 0 }
            groups.forEach { group ->
                val collapsedNow = expansion[group.check] ?: (group.advisoryOnly && blocking)
                QaIssueGroup(
                    group = group,
                    collapsed = collapsedNow,
                    onToggle = { expansion[group.check] = !collapsedNow },
                    onOpenIssue = onOpenIssue,
                    onExempt = if (canExempt) {
                        { issue ->
                            if (
                                authority.canExemptQa &&
                                isMutationCurrent()
                            ) {
                                exempting = issue
                                reason = ""
                            }
                        }
                    } else {
                        null
                    },
                    // Not offered for a group of one: "Exempt all 1 string" beside an "Exempt"
                    // button on the only row is two controls for the same click.
                    onExemptAll = if (canExempt && group.total > 1) {
                        {
                            if (authority.canExemptQa && isMutationCurrent()) {
                                exemptingAll = group.check
                                reason = ""
                            }
                        }
                    } else {
                        null
                    },
                )
            }
        }
    }

    if (canExempt) {
        val bulkTargets = exemptingAll?.let { check -> visibleIssues.filter { it.check == check } }.orEmpty()
        ExemptAllModal(
            check = exemptingAll,
            count = bulkTargets.size,
            reason = reason,
            saving = saving,
            error = exemptError,
            onReason = { reason = it },
            onClose = { exemptingAll = null; exemptError = null },
            onConfirm = {
                val check = exemptingAll ?: return@ExemptAllModal
                if (!isMutationCurrent()) return@ExemptAllModal
                val claim = mutationClaim
                    ?.takeIf { it.remainsAuthorized(currentRoute(), currentMemberships()) }
                    ?: return@ExemptAllModal
                // Read again here rather than trusting `bulkTargets` from composition: between the
                // click and this line a refresh may have landed, and exempting a finding that has
                // since been fixed writes a permanent excuse for a problem that no longer exists.
                val targets = visibleIssues
                    .filter { it.check == check }
                    .map { QaIgnoreTarget(keyId = it.keyId, locale = it.locale) }
                if (targets.isEmpty()) {
                    exemptingAll = null
                    return@ExemptAllModal
                }
                launch {
                    runProjectAction(
                        claim = claim,
                        currentRoute = currentRoute,
                        currentMemberships = currentMemberships,
                        setBusy = { saving = it },
                        transport = {
                            addIgnores(
                                project.slug,
                                CreateQaIgnoresRequest(
                                    targets = targets,
                                    check = check,
                                    reason = reason.trim(),
                                ),
                            )
                        },
                        onSuccess = {
                            exemptingAll = null
                            exemptError = null
                            onRetry()
                        },
                        onFailure = {
                            exemptError = exemptFailureMessage()
                        },
                    )
                }
            },
        )
        ExemptModal(
            issue = exempting,
            reason = reason,
            saving = saving,
            error = exemptError,
            onReason = { reason = it },
            onClose = { exempting = null; exemptError = null },
            onConfirm = {
                val issue = exempting ?: return@ExemptModal
                if (!isMutationCurrent()) return@ExemptModal
                val claim = mutationClaim
                    ?.takeIf { it.remainsAuthorized(currentRoute(), currentMemberships()) }
                    ?: return@ExemptModal
                launch {
                    runProjectAction(
                        claim = claim,
                        currentRoute = currentRoute,
                        currentMemberships = currentMemberships,
                        setBusy = { saving = it },
                        transport = {
                            addIgnore(
                                project.slug,
                                CreateQaIgnoreRequest(
                                    keyId = issue.keyId,
                                    locale = issue.locale,
                                    check = issue.check,
                                    reason = reason.trim(),
                                ),
                            )
                        },
                        onSuccess = {
                            exempting = null
                            exemptError = null
                            onRetry()
                        },
                        onFailure = {
                            exemptError = exemptFailureMessage()
                        },
                    )
                }
            },
        )
    }
}

@Composable
private fun ExemptModal(
    issue: QaIssueDto?,
    reason: String,
    saving: Boolean,
    error: String?,
    onReason: (String) -> Unit,
    onClose: () -> Unit,
    onConfirm: () -> Unit,
) {
    LModal(issue != null, t("editor.qa_page.exempt_this_string_from_the_check", "Exempt this string from the check?"), onClose, actions = {
        LButton(t("editor.qa_page.cancel", "Cancel"), onClose, variant = LButtonVariant.GHOST, enabled = !saving)
        LButton(t("editor.qa_page.exempt_it", "Exempt it"), onConfirm, enabled = reason.isNotBlank() && !saving, loading = saving)
    }) {
        issue?.let {
            LAlert(
                it.keyName,
                it.message,
                variant = LAlertVariant.INFO,
            )
        }
        LTextField(
            reason,
            onReason,
            Modifier.fillMaxWidth(),
            label = t("editor.qa_page.why_is_this_one_fine", "Why is this one fine?"),
            placeholder = t("editor.qa_page.the_german_product_name_is_intentionally", "The German product name is intentionally untranslated."),
        )
        // `live = true`: this appears only because the reader pressed "Exempt it" a moment ago, and
        // it appears *below* the button they are still on — the exact case a live region is for, and
        // the one the rest of the app already passes it for.
        error?.let {
            LAlert(
                t("editor.qa_page.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }
    }
}

/**
 * Exempting a whole check, with the count stated before the reason is asked for.
 *
 * The count comes first and is in the title because it is the part a reader can get wrong. "Exempt
 * all" beside a list showing 200 rows invites the assumption that 200 is the number; the group may
 * hold far more, and this is the last moment before that becomes a permanent decision.
 *
 * Above [MAX_QA_IGNORES_PER_REQUEST] the confirm is refused here rather than by the server, because
 * the fix is a control on the page behind this modal — narrow to one language — and a reader who
 * has already written a reason should be told before they press the button, not after.
 */
@Composable
private fun ExemptAllModal(
    check: QaCheck?,
    count: Int,
    reason: String,
    saving: Boolean,
    error: String?,
    onReason: (String) -> Unit,
    onClose: () -> Unit,
    onConfirm: () -> Unit,
) {
    val overCap = count > MAX_QA_IGNORES_PER_REQUEST
    LModal(
        check != null,
        tPlural(
            "editor.qa_page.exempt_all_title",
            count,
            "Exempt {count} string from this check?",
            "Exempt all {count} strings from this check?",
        ),
        onClose,
        actions = {
            LButton(t("editor.qa_page.cancel_all", "Cancel"), onClose, variant = LButtonVariant.GHOST, enabled = !saving)
            LButton(
                t("editor.qa_page.exempt_them", "Exempt them"),
                onConfirm,
                enabled = reason.isNotBlank() && !saving && !overCap,
                loading = saving,
            )
        },
    ) {
        check?.let {
            LAlert(
                it.displayName(),
                t("editor.qa_page.every_string_this_check_is_currently", "Every string this check is currently reporting, in the languages shown, stops " +
                    "being reported. The check itself stays on for everything else."),
                variant = LAlertVariant.INFO,
            )
        }
        if (overCap) {
            LAlert(
                t("editor.qa_page.too_many_at_once", "Too many at once"),
                tf(
                    "editor.qa_page.this_exempts_at_most_limit_strings",
                    "This exempts at most {limit} strings in one go and this check has {count}. " +
                        "Filter to one language and run it again.",
                    "limit" to MAX_QA_IGNORES_PER_REQUEST,
                    "count" to count,
                ),
                variant = LAlertVariant.WARNING,
            )
        }
        LTextField(
            reason,
            onReason,
            Modifier.fillMaxWidth(),
            label = t("editor.qa_page.why_are_these_fine", "Why are these fine?"),
            placeholder = t(
                "editor.qa_page.our_product_name_is_the_same_in_every",
                "Our product name is the same in every language.",
            ),
        )
        error?.let {
            LAlert(
                t("editor.qa_page.that_didnt_work_all", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }
    }
}

/**
 * What the modal says when an exemption is refused.
 *
 * It used to be `safeQaMessage`, and said "Couldn't refresh quality checks" — an action the reader
 * had not taken, on the one surface where they had just pressed "Exempt it". The wrong noun came
 * with dead logic: a `message?.takeIf(String::isNotBlank)?.let { … } ?: …` whose two branches
 * returned the same constant, so the chain read as though it surfaced the server's message and
 * discarded it instead. The server's message is still deliberately not repeated here — an exemption
 * failure is a write refusal, and its detail belongs in the log rather than in a modal — but the
 * sentence now names the thing that failed.
 */
private fun exemptFailureMessage(): String =
    tr("editor.qa_page.couldnt_add_this_exemption_retry", "Couldn't add this exemption. Retry?")
